import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Drawer, Typography, message } from 'antd';
import { SearchOutlined, TeamOutlined } from '@ant-design/icons';
import { useSearchParams, useNavigate } from 'react-router-dom';
import cccApi, { RecuperacaoMarcacao } from '../api/cccApi';
import { getWorkspaceSubstage } from '../utils/recuperacaoMarcacaoDisplay';
import { sectionForSubstage } from '../config/recuperacaoWorkspace';
import {
  CREATION_DATE_FILTERS,
  type CreationDatePresetKey,
  matchesCreationDateFrom,
  presetCutoffPickerValue,
} from '../utils/workspaceCreationDateFilter';
import {
  canNavigateToMatch,
  fetchCccWorkspaceSearch,
  showCccSearchNotFound,
  showCccSearchSuccess,
} from '../utils/cccWorkspaceSearch';
import { useCccAuth } from '../contexts/CccAuthContext';
import { isCccCoordenadorOrAdmin } from '../utils/cccUserManagement';
import { isPlatformAdmin } from '../utils/isPlatformAdmin';
import WonderTradeLoader from '@crm/components/Shared/WonderTradeLoader';
import RecuperacaoMarcacaoCard from '../components/RecuperacaoMarcacaoCard';
import RecuperacaoWorkspaceTables from '../components/RecuperacaoWorkspaceTables';
import { normalizeWorkspaceSubstage, SUBSTAGE_AGENDAMENTOS } from '../config/recuperacaoWorkspace';
import '@crm/components/Comercial/ComercialManagement.css';
import '../styles/prospeccao.css';
import '../styles/recuperacao.css';
import '../styles/recuperacaoInvestigate.css';
import CccGestaoModalsOverlay, { type CccGestaoModalKind } from '../components/CccGestaoModalsOverlay';
import CccPedirAssinaturaDigitalOverlay from '../components/CccPedirAssinaturaDigitalOverlay';
import CccAlterarComercialModal from '../components/CccAlterarComercialModal';
import CccReverterModal, { CccComercialUserOption } from '../components/CccReverterModal';
import CccBulkSelectionBar from '../components/CccBulkSelectionBar';
import CccWorkspaceDuplicateIndicator, {
  type CccWorkspaceDuplicateFlags,
} from '../components/CccWorkspaceDuplicateIndicator';
import RecuperacaoInvestigateModal, {
  RecuperacaoInvestigateQuickSearchSeed,
  RecuperacaoInvestigateRow,
} from '../components/RecuperacaoInvestigateModal';
import { mainApiUrl } from '../config';

const { Text } = Typography;

const POOL_PAGE_SIZE = 80;

type WorkspaceUserOption = { id: number; name: string; username?: string; email?: string };

const marcacaoCreatedAtIso = (m: RecuperacaoMarcacao): string | undefined => {
  if (m.created_at) return m.created_at;
  if (m.date) return `${m.date}T00:00:00`;
  return undefined;
};

const claimedById = (m: RecuperacaoMarcacao): number | null => {
  const raw = m.recuperacao_claim?.claimed_by;
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const RecuperacaoBoard: React.FC = () => {
  const { user } = useCccAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [workspaceItems, setWorkspaceItems] = useState<RecuperacaoMarcacao[]>([]);
  const [poolItems, setPoolItems] = useState<RecuperacaoMarcacao[]>([]);
  const [poolTotal, setPoolTotal] = useState(0);
  const [poolHasMore, setPoolHasMore] = useState(false);
  const [poolLoading, setPoolLoading] = useState(false);
  const [poolLoadingMore, setPoolLoadingMore] = useState(false);
  const poolOffsetRef = useRef(0);
  const poolSearchRef = useRef('');
  const poolSentinelRef = useRef<HTMLDivElement | null>(null);
  const [search, setSearch] = useState('');
  const [creationDateFrom, setCreationDateFrom] = useState('');
  const [poolHighlightId, setPoolHighlightId] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarSearchTerm, setSidebarSearchTerm] = useState('');
  const [workspaceUsers, setWorkspaceUsers] = useState<WorkspaceUserOption[]>([]);
  const [workspaceUserId, setWorkspaceUserId] = useState<number | 'all' | null>(null);
  const [claimingId, setClaimingId] = useState<string | null>(null);
  const [retrocederTargetId, setRetrocederTargetId] = useState<string | null>(null);
  const [retrocedendo, setRetrocedendo] = useState(false);
  const [reverterTargetId, setReverterTargetId] = useState<string | null>(null);
  const [revertendoComercial, setRevertendoComercial] = useState(false);
  const [comercialUsers, setComercialUsers] = useState<CccComercialUserOption[]>([]);
  const [queueOpen, setQueueOpen] = useState(false);
  const [gestaoModal, setGestaoModal] = useState<CccGestaoModalKind | null>(null);
  const [gestaoModalNonce, setGestaoModalNonce] = useState(0);
  const [showPedirAssinaturaDigital, setShowPedirAssinaturaDigital] = useState(false);
  const [pedirAssinaturaNonce, setPedirAssinaturaNonce] = useState(0);
  const [bulkSelectedIds, setBulkSelectedIds] = useState<Set<string>>(() => new Set());
  const [alterarComercialOpen, setAlterarComercialOpen] = useState(false);
  const [alterarComercialSubmitting, setAlterarComercialSubmitting] = useState(false);
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [investigateQuickSearch, setInvestigateQuickSearch] =
    useState<RecuperacaoInvestigateQuickSearchSeed | null>(null);
  const [duplicateFlags, setDuplicateFlags] = useState<Record<string, CccWorkspaceDuplicateFlags>>({});
  const [isMobileOrTablet] = useState(() => {
    if (typeof window === 'undefined') return false;
    const hasTouch =
      (typeof navigator !== 'undefined' && (navigator.maxTouchPoints || 0) > 0) ||
      'ontouchstart' in window;
    const isTabletWidth = window.innerWidth <= 1100;
    const isSmallScreen = window.matchMedia('(max-width: 768px)').matches;
    return hasTouch && (isTabletWidth || isSmallScreen);
  });

  const canManageWorkspaceScope = useMemo(() => isCccCoordenadorOrAdmin(user), [user]);

  const canReassignComercial = useMemo(() => {
    if (isPlatformAdmin(user)) return true;
    const role = String(user?.role || '').trim().toLowerCase();
    const tenant = String(user?.tenant || '').trim().toLowerCase();
    const businessArea = String(user?.business_area || '').trim().toLowerCase();
    if (role.includes('admin') || tenant === 'admin' || businessArea === 'admin') return true;
    return role.includes('gerente') || role.includes('manager');
  }, [user]);

  const currentUserId = useMemo(() => {
    const raw = user?.id;
    if (raw === null || raw === undefined || raw === '') return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [user]);

  const loadWorkspace = useCallback(async (creationFrom = creationDateFrom) => {
    const res = await cccApi.getRecuperacaoMarcacoes({
      pool: 'workspace',
      creation_date_from: creationFrom || undefined,
    });
    const list = Array.isArray(res.marcacoes) ? res.marcacoes : [];
    setWorkspaceItems(list);
    return list;
  }, [creationDateFrom]);

  const loadPoolPage = useCallback(
    async (offset: number, append: boolean, opts?: { search?: string; creationFrom?: string }) => {
      const searchQuery = opts?.search ?? poolSearchRef.current;
      const creationFrom = opts?.creationFrom ?? creationDateFrom;
      if (!append) setPoolLoading(true);
      else setPoolLoadingMore(true);
      try {
        const res = await cccApi.getRecuperacaoMarcacoes({
          pool: 'available',
          limit: POOL_PAGE_SIZE,
          offset,
          summary: true,
          search: searchQuery || undefined,
          creation_date_from: creationFrom || undefined,
        });
        const page = Array.isArray(res.marcacoes) ? res.marcacoes : [];
        const total = res.pagination?.total ?? res.stats?.available ?? page.length;
        setPoolTotal(total);
        setPoolHasMore(res.pagination?.has_more ?? offset + page.length < total);
        poolOffsetRef.current = offset + page.length;
        setPoolItems((prev) => (append ? [...prev, ...page] : page));
      } catch (e) {
        console.error('[RecuperacaoBoard] pool load failed', e);
        if (!append) {
          setPoolItems([]);
          setPoolTotal(0);
          setPoolHasMore(false);
          poolOffsetRef.current = 0;
        }
        message.error('Não foi possível carregar o Pool de Recuperação');
      } finally {
        setPoolLoading(false);
        setPoolLoadingMore(false);
      }
    },
    [creationDateFrom]
  );

  const loadDuplicateFlags = useCallback(async () => {
    try {
      const res = await cccApi.getCccWorkspaceDuplicateFlags();
      if (res.success) {
        setDuplicateFlags(res.recuperacao || {});
      }
    } catch {
      /* non-blocking */
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    poolSearchRef.current = '';
    try {
      await Promise.all([loadWorkspace(), loadPoolPage(0, false)]);
    } catch (e) {
      console.error('[RecuperacaoBoard] load failed', e);
      message.error('Não foi possível carregar marcações de Recuperação');
      setWorkspaceItems([]);
      setPoolItems([]);
      setPoolTotal(0);
      setPoolHasMore(false);
      poolOffsetRef.current = 0;
    } finally {
      setLoading(false);
    }
  }, [loadWorkspace, loadPoolPage]);

  useEffect(() => {
    load();
  }, [load]);

  // Duplicate flags: defer until Pool is opened (avoid second full scan on mount).
  useEffect(() => {
    if (!queueOpen) return;
    if (Object.keys(duplicateFlags).length > 0) return;
    void loadDuplicateFlags();
  }, [queueOpen, duplicateFlags, loadDuplicateFlags]);

  const prevCreationDateRef = useRef(creationDateFrom);
  useEffect(() => {
    if (prevCreationDateRef.current === creationDateFrom) return;
    prevCreationDateRef.current = creationDateFrom;
    if (loading) return;
    poolSearchRef.current = '';
    void loadWorkspace(creationDateFrom);
    void loadPoolPage(0, false, { creationFrom: creationDateFrom });
  }, [creationDateFrom, loading, loadWorkspace, loadPoolPage]);

  useEffect(() => {
    const node = poolSentinelRef.current;
    if (!node || !poolHasMore || poolLoading || poolLoadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        void loadPoolPage(poolOffsetRef.current, true);
      },
      { root: node.parentElement, rootMargin: '120px', threshold: 0 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [poolHasMore, poolLoading, poolLoadingMore, loadPoolPage, poolItems.length, queueOpen]);

  useEffect(() => {
    if (!canManageWorkspaceScope) {
      setWorkspaceUserId(currentUserId);
      return;
    }
    if (workspaceUserId === null) setWorkspaceUserId('all');
  }, [canManageWorkspaceScope, currentUserId, workspaceUserId]);

  useEffect(() => {
    if (!canManageWorkspaceScope) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await cccApi.getCccDashboardUsers();
        if (cancelled || !res.success) return;
        const options = (res.users || [])
          .map((u) => ({
            id: Number(u.id),
            name: String(u.name || u.email || `Utilizador ${u.id}`),
            username: undefined,
            email: u.email ? String(u.email) : undefined,
          }))
          .filter((u: WorkspaceUserOption) => Number.isFinite(u.id))
          .sort((a, b) => a.name.localeCompare(b.name, 'pt'));
        setWorkspaceUsers(options);
      } catch {
        if (!cancelled) setWorkspaceUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canManageWorkspaceScope]);

  const filteredWorkspaceItems = useMemo(
    () =>
      workspaceItems.filter((m) =>
        matchesCreationDateFrom(marcacaoCreatedAtIso(m), creationDateFrom)
      ),
    [workspaceItems, creationDateFrom]
  );

  const disponiveis = poolItems;

  const mine = useMemo(() => {
    if (!canManageWorkspaceScope) {
      return filteredWorkspaceItems.filter((m) => m.is_mine);
    }
    if (workspaceUserId === 'all' || workspaceUserId === null) {
      return filteredWorkspaceItems.filter((m) => claimedById(m) !== null);
    }
    return filteredWorkspaceItems.filter((m) => claimedById(m) === workspaceUserId);
  }, [filteredWorkspaceItems, canManageWorkspaceScope, workspaceUserId]);

  const allItems = useMemo(() => [...filteredWorkspaceItems, ...poolItems], [filteredWorkspaceItems, poolItems]);

  const fechoCount = useMemo(
    () =>
      mine.filter((m) => {
        const substage = normalizeWorkspaceSubstage(getWorkspaceSubstage(m));
        return sectionForSubstage(substage)?.key === 'fecho';
      }).length,
    [mine]
  );

  const stats = useMemo(
    () => ({
      available: poolTotal,
      mine: mine.length,
      fecho: fechoCount,
    }),
    [poolTotal, mine.length, fechoCount]
  );

  const workspaceSelectionLabel = useMemo(() => {
    if (!canManageWorkspaceScope) return '';
    if (workspaceUserId === 'all' || workspaceUserId === null) return 'Toda a Equipa';
    const match = workspaceUsers.find((u) => u.id === workspaceUserId);
    return match ? match.name : 'Utilizador selecionado';
  }, [canManageWorkspaceScope, workspaceUserId, workspaceUsers]);

  const gestaoFilterUserId = useMemo(() => {
    if (!canManageWorkspaceScope) return currentUserId;
    if (workspaceUserId === 'all' || workspaceUserId === null) return currentUserId;
    return workspaceUserId;
  }, [canManageWorkspaceScope, currentUserId, workspaceUserId]);

  const filteredSidebarUsers = useMemo(() => {
    if (!sidebarSearchTerm.trim()) return workspaceUsers;
    const searchLower = sidebarSearchTerm.toLowerCase();
    return workspaceUsers.filter((u) => {
      const name = (u.name || u.username || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return name.includes(searchLower) || email.includes(searchLower);
    });
  }, [workspaceUsers, sidebarSearchTerm]);

  const applyCreationDatePreset = (key: CreationDatePresetKey) => {
    setCreationDateFrom(presetCutoffPickerValue(key));
  };

  const clearFilters = () => {
    setSearch('');
    setCreationDateFrom('');
    setPoolHighlightId(null);
    poolSearchRef.current = '';
    if (canManageWorkspaceScope) {
      setWorkspaceUserId('all');
    } else if (currentUserId) {
      setWorkspaceUserId(currentUserId);
    }
    void loadWorkspace('');
    void loadPoolPage(0, false, { search: '', creationFrom: '' });
  };

  const openGestaoModal = (kind: CccGestaoModalKind) => {
    setGestaoModal(kind);
    setGestaoModalNonce((n) => n + 1);
  };

  const openPedirAssinaturaDigital = () => {
    setShowPedirAssinaturaDigital(true);
    setPedirAssinaturaNonce((n) => n + 1);
  };

  const handleWorkspaceSearch = async () => {
    const q = search.trim();
    if (!q) {
      message.warning('Introduza empresa, contacto ou NIF para pesquisar.');
      return;
    }
    try {
      const matches = await fetchCccWorkspaceSearch('recuperacao', q);
      if (!matches.length) {
        showCccSearchNotFound(q);
        return;
      }
      const match = matches[0];
      const nav = canNavigateToMatch(match, { canManageWorkspaceScope, currentUserId });
      if (!nav.ok) {
        message.warning(nav.reason);
        return;
      }
      showCccSearchSuccess(match);
      if (match.workspace === 'prospeccao') {
        if (matches.length > 1) {
          message.info(`Foram encontrados ${matches.length} resultados. A abrir o primeiro (Prospeção).`);
        }
        navigate(`/prospeccao?ref=${encodeURIComponent(match.id)}`);
        return;
      }
      setCreationDateFrom('');
      setPoolHighlightId(null);
      if (canManageWorkspaceScope) {
        setWorkspaceUserId(match.owner_user_id ?? 'all');
      }
      if (match.is_available) {
        poolSearchRef.current = q;
        await Promise.all([loadWorkspace(), loadPoolPage(0, false, { search: q })]);
      } else {
        await loadWorkspace();
      }
      if (match.is_available) {
        setQueueOpen(true);
        setPoolHighlightId(match.id);
        window.requestAnimationFrame(() => {
          document
            .querySelector(`[data-pool-marcacao-id="${match.id}"]`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        });
        if (matches.length > 1) {
          message.info(`Foram encontrados ${matches.length} resultados. A abrir o primeiro (Pool).`);
        }
        return;
      }
      setWorkspaceFocus({
        marcacaoId: match.id,
        substage: normalizeWorkspaceSubstage(match.substage),
      });
      if (matches.length > 1) {
        message.info(`Foram encontrados ${matches.length} resultados. A abrir o primeiro.`);
      }
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ||
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao pesquisar';
      message.error(msg);
    }
  };

  const retrocederMarcacao = useMemo(
    () =>
      retrocederTargetId
        ? mine.find((m) => (m.marcacao_id || String(m.id)) === retrocederTargetId)
        : undefined,
    [mine, retrocederTargetId]
  );

  const reverterMarcacao = useMemo(
    () =>
      reverterTargetId
        ? mine.find((m) => (m.marcacao_id || String(m.id)) === reverterTargetId)
        : undefined,
    [mine, reverterTargetId]
  );

  useEffect(() => {
    if (!canReassignComercial) return;
    let cancelled = false;
    (async () => {
      try {
        const token =
          localStorage.getItem('jwt_token') || localStorage.getItem('cognito_access_token');
        const res = await fetch(mainApiUrl('/api/callcenter/comercial-users?include_hidden=1'), {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const list = Array.isArray(json?.comercial_users) ? json.comercial_users : [];
        const options = list
          .map((u: Record<string, unknown>) => ({
            id: Number(u.id),
            name: String(u.display_name || u.name || u.username || u.email || `Utilizador ${u.id}`),
            username: u.username ? String(u.username) : undefined,
            email: u.email ? String(u.email) : undefined,
          }))
          .filter((u: CccComercialUserOption) => Number.isFinite(u.id))
          .sort((a: CccComercialUserOption, b: CccComercialUserOption) => a.name.localeCompare(b.name, 'pt'));
        if (!cancelled) setComercialUsers(options);
      } catch {
        if (!cancelled) setComercialUsers([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canReassignComercial]);

  const handleClaim = async (marcacaoId: string) => {
    setClaimingId(marcacaoId);
    try {
      const res = await cccApi.claimRecuperacaoMarcacao(marcacaoId);
      if (res.success) {
        message.success('Marcação iniciada na sua área de trabalho');
        await load();
        const claimed = res.marcacao;
        const focusSubstage = normalizeWorkspaceSubstage(
          claimed ? getWorkspaceSubstage(claimed) : SUBSTAGE_AGENDAMENTOS
        );
        setWorkspaceFocus({ marcacaoId, substage: focusSubstage });
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível iniciar a marcação';
      message.error(msg);
      await load();
    } finally {
      setClaimingId(null);
    }
  };

  const handleConfirmRetroceder = async () => {
    if (!retrocederTargetId) return;
    setRetrocedendo(true);
    try {
      await cccApi.releaseRecuperacaoMarcacao(retrocederTargetId);
      message.success('Marcação retrocedida com sucesso');
      setRetrocederTargetId(null);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível retroceder a marcação';
      message.error(msg);
    } finally {
      setRetrocedendo(false);
    }
  };

  const handleConfirmReverter = async (payload: { comercial_user_id?: number }) => {
    if (!reverterTargetId) return;
    setRevertendoComercial(true);
    try {
      await cccApi.reverterRecuperacaoMarcacao(reverterTargetId, payload);
      message.success('Marcação revertida para o Comercial');
      setReverterTargetId(null);
      await load();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível reverter a marcação';
      message.error(msg);
    } finally {
      setRevertendoComercial(false);
    }
  };

  const [workspaceFocus, setWorkspaceFocus] = useState<{
    marcacaoId: string;
    substage: string;
    infoTab?: 'historico';
  } | null>(null);
  const urlFocusHandledRef = useRef<string | null>(null);

  const patchWorkspaceMarcacao = useCallback((updated: RecuperacaoMarcacao) => {
    const mid = updated.marcacao_id || String(updated.id);
    setWorkspaceItems((prev) =>
      prev.map((m) => ((m.marcacao_id || String(m.id)) === mid ? { ...m, ...updated } : m))
    );
    setWorkspaceFocus({
      marcacaoId: mid,
      substage: normalizeWorkspaceSubstage(getWorkspaceSubstage(updated)),
    });
  }, []);

  useEffect(() => {
    setWorkspaceFocus(null);
    setBulkSelectedIds(new Set());
  }, [workspaceUserId, creationDateFrom]);

  useEffect(() => {
    const marcacaoId = searchParams.get('marcacao')?.trim();
    if (!marcacaoId) {
      urlFocusHandledRef.current = null;
      return;
    }

    if (creationDateFrom) {
      setSearch('');
      setCreationDateFrom('');
      return;
    }

    if (loading) return;

    const applyFocus = (m: RecuperacaoMarcacao) => {
      const tab = searchParams.get('tab')?.trim();
      setWorkspaceFocus({
        marcacaoId,
        substage: normalizeWorkspaceSubstage(getWorkspaceSubstage(m)),
        ...(tab === 'historico' ? { infoTab: 'historico' as const } : {}),
      });
      setQueueOpen(false);
    };

    const m = allItems.find(
      (x) => (x.marcacao_id || String(x.id)) === marcacaoId && x.is_mine
    );
    if (m) {
      urlFocusHandledRef.current = marcacaoId;
      applyFocus(m);
      return;
    }

    if (urlFocusHandledRef.current === marcacaoId) return;
    urlFocusHandledRef.current = marcacaoId;

    let cancelled = false;
    (async () => {
      try {
        const res = await cccApi.getRecuperacaoMarcacao(marcacaoId);
        if (cancelled || !res.success || !res.marcacao?.is_mine) return;
        await load();
        if (cancelled) return;
        applyFocus(res.marcacao);
      } catch {
        /* list may still load on next refresh */
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, allItems, loading, creationDateFrom, load, loadWorkspace]);

  const handleSubstageChange = async (marcacaoId: string, substage: string): Promise<boolean> => {
    try {
      const res = await cccApi.updateRecuperacaoSubstage(marcacaoId, substage);
      if (!res.success) {
        message.error(res.message || 'Erro ao atualizar estado');
        return false;
      }
      message.success('Estado atualizado');
      await load();
      return true;
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao atualizar estado';
      message.error(msg);
      return false;
    }
  };

  const toggleBulkSelect = useCallback((marcacaoId: string, checked: boolean) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(marcacaoId);
      else next.delete(marcacaoId);
      return next;
    });
  }, []);

  const toggleBulkSelectPage = useCallback((marcacaoIds: string[], checked: boolean) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      marcacaoIds.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const clearBulkSelection = useCallback(() => setBulkSelectedIds(new Set()), []);

  const openAlterarComercial = useCallback(() => {
    if (bulkSelectedIds.size === 0) {
      message.warning('Selecione pelo menos um cliente na área de trabalho.');
      return;
    }
    setAlterarComercialOpen(true);
  }, [bulkSelectedIds.size]);

  const handleConfirmAlterarComercial = async (userId: number) => {
    const ids = Array.from(bulkSelectedIds);
    if (!ids.length) return;
    setAlterarComercialSubmitting(true);
    try {
      const res = await cccApi.bulkReassignRecuperacaoMarcacoes(ids, userId);
      const count = res.updated_count ?? res.updated?.length ?? 0;
      if (count > 0) {
        message.success(
          count === 1
            ? 'Comercial alterado com sucesso.'
            : `${count} clientes com comercial alterado.`
        );
        clearBulkSelection();
        setAlterarComercialOpen(false);
        await load();
      } else {
        message.warning('Nenhum cliente foi alterado.');
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Não foi possível alterar o comercial';
      message.error(msg);
    } finally {
      setAlterarComercialSubmitting(false);
    }
  };

  const handleAddNote = async (marcacaoId: string, text: string) => {
    try {
      const res = await cccApi.addRecuperacaoNote(marcacaoId, text);
      if (res.success) {
        message.success('Nota adicionada');
        await load();
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao guardar nota';
      message.error(msg);
    }
  };

  const openInvestigateModal = useCallback((seed?: RecuperacaoInvestigateQuickSearchSeed | null) => {
    setInvestigateQuickSearch(seed ?? null);
    setInvestigateOpen(true);
  }, []);

  const closeInvestigateModal = useCallback(() => {
    setInvestigateOpen(false);
    setInvestigateQuickSearch(null);
  }, []);

  const handleQuickInvestigate = useCallback(
    (seed: RecuperacaoInvestigateQuickSearchSeed) => {
      openInvestigateModal(seed);
    },
    [openInvestigateModal]
  );

  const handleInvestigateOpenMarcacao = async (row: RecuperacaoInvestigateRow) => {
    closeInvestigateModal();

    if (row.workspace === 'prospeccao') {
      const leadId = row.lead_id || String(row.id);
      navigate(`/prospeccao?ref=${encodeURIComponent(leadId)}`);
      message.success(`Lead ${leadId} aberto em Prospeção.`);
      return;
    }

    const marcacaoId = row.marcacao_id;
    if (!marcacaoId) return;

    setCreationDateFrom('');
    setPoolHighlightId(null);

    if (row.is_available) {
      poolSearchRef.current = '';
      setQueueOpen(true);
      await Promise.all([loadWorkspace(), loadPoolPage(0, false)]);
      setPoolHighlightId(marcacaoId);
      window.requestAnimationFrame(() => {
        document
          .querySelector(`[data-pool-marcacao-id="${marcacaoId}"]`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      message.success(`Marcação ${marcacaoId} destacada na Pool.`);
      return;
    }

    if (canManageWorkspaceScope && row.ccc_claimed_by != null) {
      setWorkspaceUserId(row.ccc_claimed_by);
    }
    await loadWorkspace();
    setWorkspaceFocus({
      marcacaoId,
      substage: normalizeWorkspaceSubstage(row.ccc_substage || 'por-contactar-iniciado-atribuido'),
    });
    message.success(`Marcação ${marcacaoId} aberta na área de trabalho.`);
  };

  if (loading && workspaceItems.length === 0) {
    return (
      <div className="rec-page">
        <WonderTradeLoader message="A carregar Recuperação..." />
      </div>
    );
  }

  return (
    <div className="rec-page gestao-marcacoes prosp-page-scope">
      <header className="rec-hero rec-hero--compact">
        <button
          type="button"
          className="rec-investigate-fab"
          onClick={() => openInvestigateModal()}
          aria-label="Investigar marcações"
          title="Investigar Marcações"
        >
          <SearchOutlined />
        </button>
        <div className="rec-hero-top">
          <div className="rec-hero-text">
            <span className="rec-hero-eyebrow">CallCenter Comercial</span>
            <div
              className="gestao-marcacoes-header-title-row prosp-hero-title-row"
              style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}
            >
              {canManageWorkspaceScope && (
                <Button
                  type="text"
                  className="gestao-marcacoes-sidebar-trigger"
                  icon={<TeamOutlined style={{ fontSize: 10 }} />}
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Abrir seleção de utilizador da equipa CCC"
                  style={{
                    backgroundColor: '#ff4d4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '3px',
                    padding: 0,
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    minHeight: 20,
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1,
                    boxShadow: '0 1px 2px rgba(255, 77, 79, 0.22)',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff7875';
                    e.currentTarget.style.boxShadow = '0 2px 5px rgba(255, 77, 79, 0.32)';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ff4d4f';
                    e.currentTarget.style.boxShadow = '0 1px 2px rgba(255, 77, 79, 0.22)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                />
              )}
              <h1 className="rec-hero-title">Recuperação</h1>
              {canManageWorkspaceScope && workspaceSelectionLabel && (
                <div className="management-viewing-user">
                  <div
                    className="user-color management-user-dot"
                    style={{
                      backgroundColor:
                        workspaceUserId === 'all' || workspaceUserId === null
                          ? '#1890ff'
                          : Number(workspaceUserId) % 2 === 0
                            ? '#ff001e'
                            : '#cccccc',
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <span className="management-user-indicator">{workspaceSelectionLabel}</span>
                </div>
              )}
            </div>
          </div>
          <div className="rec-kpis">
            <div className="rec-kpi">
              <span className="rec-kpi-value">{stats.available}</span>
              <span className="rec-kpi-label">Pool</span>
            </div>
            <div className="rec-kpi">
              <span className="rec-kpi-value">{stats.mine}</span>
              <span className="rec-kpi-label">Na minha área</span>
            </div>
            <div className="rec-kpi">
              <span className="rec-kpi-value rec-kpi-value--fecho">{stats.fecho}</span>
              <span className="rec-kpi-label">Fecho</span>
            </div>
          </div>
        </div>
        <div className="rec-toolbar">
          <div className="rec-toolbar-group">
            <input
              type="search"
              className="rec-input"
              placeholder="Pesquisar em Recuperação e Prospeção (empresa, contacto ou NIF)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && void handleWorkspaceSearch()}
            />
            <button type="button" className="rec-btn rec-btn-ghost" onClick={() => void handleWorkspaceSearch()}>
              Procurar
            </button>
            <div className="prosp-date-filters" role="group" aria-label="Filtrar por data de criação">
              <span className="prosp-date-filters-label">Data de criação</span>
              <div className="prosp-date-filters-buttons">
                {CREATION_DATE_FILTERS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`prosp-date-filter-btn ${
                      creationDateFrom === presetCutoffPickerValue(option.key) ? 'is-active' : ''
                    }`}
                    onClick={() => applyCreationDatePreset(option.key)}
                    aria-pressed={creationDateFrom === presetCutoffPickerValue(option.key)}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <label className="prosp-date-picker-field">
                <span className="prosp-date-filters-label">A partir de</span>
                <input
                  type="date"
                  className={`rec-input prosp-date-picker ${creationDateFrom ? 'is-active' : ''}`}
                  value={creationDateFrom}
                  onChange={(e) => setCreationDateFrom(e.target.value)}
                  aria-label="Filtrar marcações criadas a partir desta data"
                />
              </label>
            </div>
          </div>
          <div className="rec-toolbar-group rec-toolbar-group--actions">
            <button type="button" className="rec-btn rec-btn-ghost" onClick={clearFilters}>
              Limpar Filtros
            </button>
            <button type="button" className="rec-btn rec-btn-primary" onClick={() => load()}>
              Atualizar
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-minhas-propostas"
              onClick={() => openGestaoModal('minhas-propostas')}
            >
              Minhas Propostas
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-meus-contratos"
              onClick={() => openGestaoModal('meus-contratos')}
            >
              Meus Contratos
            </button>
            <button
              type="button"
              className="rec-btn rec-btn-pedir-assinatura"
              onClick={openPedirAssinaturaDigital}
            >
              ✒ Pedir Assinatura Digital
            </button>
            {canReassignComercial && (
              <button
                type="button"
                className="rec-btn rec-btn-ghost rec-btn-alterar-comercial"
                onClick={openAlterarComercial}
              >
                Alterar Comercial
                {bulkSelectedIds.size > 0 ? ` (${bulkSelectedIds.size})` : ''}
              </button>
            )}
          </div>
        </div>
      </header>

      {canReassignComercial && (
        <CccBulkSelectionBar
          selectedCount={bulkSelectedIds.size}
          onClear={clearBulkSelection}
          onAlterarComercial={openAlterarComercial}
        />
      )}

      <div className={`rec-workspace ${queueOpen ? '' : 'rec-workspace--queue-closed'}`}>
        <aside className={`rec-queue-rail ${queueOpen ? 'is-open' : 'is-collapsed'}`}>
          {queueOpen ? (
            <>
              <div className="rec-queue-head">
                <div className="rec-queue-head-row">
                  <div className="rec-queue-head-label">
                    <h2 className="rec-queue-title">Pool</h2>
                    <span className="rec-badge">{poolTotal}</span>
                  </div>
                  <button
                    type="button"
                    className="rec-queue-collapse-btn"
                    onClick={() => setQueueOpen(false)}
                    aria-label="Recolher painel Pool"
                    title="Recolher"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
                      <path
                        d="M10 3L5 8L10 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <p className="rec-queue-hint">Clique numa empresa para iniciar.</p>
              </div>
              <div className="rec-queue-list">
                {poolLoading && disponiveis.length === 0 ? (
                  <p className="rec-queue-empty">A carregar Pool…</p>
                ) : poolTotal === 0 ? (
                  <p className="rec-queue-empty">Nenhuma marcação disponível.</p>
                ) : (
                  <>
                    {disponiveis.map((m) => {
                      const id = m.marcacao_id || String(m.id);
                      return (
                        <div
                          key={id}
                          data-pool-marcacao-id={id}
                          className={
                            poolHighlightId === id
                              ? 'rec-queue-item-wrap is-search-hit'
                              : 'rec-queue-item-wrap'
                          }
                        >
                          <RecuperacaoMarcacaoCard
                            marcacao={m}
                            claiming={claimingId === id}
                            onClaim={() => handleClaim(id)}
                            onQuickInvestigate={handleQuickInvestigate}
                            duplicateFlags={duplicateFlags[id]}
                          />
                        </div>
                      );
                    })}
                    {poolHasMore ? (
                      <div ref={poolSentinelRef} className="rec-queue-load-more" aria-hidden>
                        {poolLoadingMore ? 'A carregar mais…' : ''}
                      </div>
                    ) : null}
                    {poolTotal > disponiveis.length ? (
                      <p className="rec-queue-more-hint">
                        A mostrar {disponiveis.length} de {poolTotal}. Deslize para carregar mais.
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </>
          ) : (
            <button
              type="button"
              className="rec-queue-rail-collapsed"
              onClick={() => setQueueOpen(true)}
              aria-label={`Expandir Pool (${poolTotal})`}
              title="Expandir Pool"
            >
              <span className="rec-queue-rail-accent" aria-hidden />
              <span className="rec-queue-rail-collapsed-inner">
                <span className="rec-queue-rail-expand-icon">
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                    <path
                      d="M6 3L11 8L6 13"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <span className="rec-queue-rail-vertical-label">Pool</span>
                <span className="rec-queue-rail-count">{poolTotal}</span>
              </span>
            </button>
          )}
        </aside>

        <main className="rec-desk">
          <div className="rec-desk-body">
            {mine.length === 0 ? (
              <div className="rec-desk-empty">
                <div className="rec-desk-empty-icon" aria-hidden />
                <p className="rec-desk-empty-title">Área de trabalho vazia</p>
                <p className="rec-desk-empty-text">
                  Escolha uma empresa em Pool à esquerda para iniciar. Depois clique numa linha
                  para ver o perfil do cliente, alterar estado e registar notas.
                </p>
              </div>
            ) : (
              <RecuperacaoWorkspaceTables
                items={mine}
                onSubstageChange={handleSubstageChange}
                onRetroceder={setRetrocederTargetId}
                onReverterParaComercial={
                  canReassignComercial ? setReverterTargetId : undefined
                }
                onAddNote={handleAddNote}
                onMarcacaoRefresh={load}
                onMarcacaoPatched={patchWorkspaceMarcacao}
                workspaceFocus={workspaceFocus}
                onWorkspaceFocusDone={() => setWorkspaceFocus(null)}
                bulkSelectEnabled={canReassignComercial}
                bulkSelectedIds={bulkSelectedIds}
                onBulkToggle={toggleBulkSelect}
                onBulkTogglePage={toggleBulkSelectPage}
                onQuickInvestigate={handleQuickInvestigate}
                duplicateFlags={duplicateFlags}
              />
            )}
          </div>
        </main>
      </div>

      {retrocederTargetId && (
        <div
          className="rec-modal-backdrop"
          onClick={() => !retrocedendo && setRetrocederTargetId(null)}
          role="presentation"
        >
          <div
            className="rec-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-labelledby="rec-modal-title"
          >
            <div className="rec-modal-head">
              <h2 id="rec-modal-title" className="rec-modal-title">
                Confirmar re-envio para a Pool
              </h2>
            </div>
            <div className="rec-modal-body">
              <p className="rec-modal-text">
                A marcação volta à fila Pool para toda a equipa.
              </p>
              {retrocederMarcacao?.nome_empresa && (
                <p className="rec-modal-empresa">{retrocederMarcacao.nome_empresa}</p>
              )}
              <div className="rec-modal-actions">
                <button
                  type="button"
                  className="rec-btn rec-btn-ghost"
                  disabled={retrocedendo}
                  onClick={() => setRetrocederTargetId(null)}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="rec-btn rec-btn-primary"
                  disabled={retrocedendo}
                  onClick={handleConfirmRetroceder}
                >
                  {retrocedendo ? 'A re-enviar para a Pool…' : 'Re-enviar para a Pool'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {canManageWorkspaceScope && (
        <Drawer
          title={<Text strong style={{ fontSize: 20 }}>Equipa</Text>}
          placement="left"
          closable
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={isMobileOrTablet ? '80vw' : 300}
          style={{
            borderTopRightRadius: 18,
            borderBottomRightRadius: 18,
          }}
          headerStyle={{
            borderBottom: '1px solid #f0f0f0',
            background: '#fff',
            borderTopRightRadius: 18,
          }}
          bodyStyle={{
            padding: '16px',
            background: '#fff',
            maxHeight: '100vh',
            overflowY: 'auto',
          }}
        >
          <div className="search-section" style={{ marginBottom: '16px' }}>
            <div className="search-input-wrapper">
              <input
                type="text"
                placeholder="Pesquisar utilizador..."
                value={sidebarSearchTerm}
                onChange={(e) => setSidebarSearchTerm(e.target.value)}
                className="user-search-input"
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d9d9d9',
                }}
              />
            </div>
          </div>

          <div className="user-list">
            <div
              className={`user-item ${workspaceUserId === 'all' || workspaceUserId === null ? 'selected' : ''}`}
              onClick={() => {
                setWorkspaceUserId('all');
                setDrawerOpen(false);
              }}
              style={{
                cursor: 'pointer',
                padding: '12px',
                marginBottom: '8px',
                borderRadius: '8px',
                border:
                  workspaceUserId === 'all' || workspaceUserId === null
                    ? '2px solid #1890ff'
                    : '1px solid #f0f0f0',
                backgroundColor:
                  workspaceUserId === 'all' || workspaceUserId === null ? '#e6f7ff' : '#fff',
                transition: 'all 0.2s',
              }}
            >
              <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div
                  className="user-color"
                  style={{
                    backgroundColor: '#1890ff',
                    width: '20px',
                    height: '20px',
                    borderRadius: '50%',
                    flexShrink: 0,
                  }}
                />
                <span className="user-name" style={{ flex: 1, fontWeight: 500 }}>
                  Toda a Equipa
                </span>
              </div>
            </div>

            {filteredSidebarUsers.map((option) => (
              <div
                key={option.id}
                className={`user-item ${workspaceUserId === option.id ? 'selected' : ''}`}
                onClick={() => {
                  setWorkspaceUserId(option.id);
                  setDrawerOpen(false);
                }}
                style={{
                  cursor: 'pointer',
                  padding: '12px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  border: workspaceUserId === option.id ? '2px solid #1890ff' : '1px solid #f0f0f0',
                  backgroundColor: workspaceUserId === option.id ? '#e6f7ff' : '#fff',
                  transition: 'all 0.2s',
                }}
              >
                <div className="user-info" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    className="user-color"
                    style={{
                      backgroundColor: option.id % 2 === 0 ? '#ff001e' : '#cccccc',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      flexShrink: 0,
                    }}
                  />
                  <span className="user-name" style={{ flex: 1, fontWeight: 500 }}>
                    {option.name || option.username || 'Unknown'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Drawer>
      )}

      {gestaoModal && (
        <CccGestaoModalsOverlay
          key={`${gestaoModal}-${gestaoModalNonce}-${gestaoFilterUserId ?? 'none'}`}
          modal={gestaoModal}
          filterUserId={gestaoFilterUserId}
          filterUserLabel={workspaceSelectionLabel}
          gestaoCccRecuperacao
          onClose={() => setGestaoModal(null)}
        />
      )}

      {showPedirAssinaturaDigital && (
        <CccPedirAssinaturaDigitalOverlay
          key={`pedir-assinatura-${pedirAssinaturaNonce}`}
          gestaoCccRecuperacao
          onClose={() => setShowPedirAssinaturaDigital(false)}
        />
      )}

      {canReassignComercial && (
        <CccAlterarComercialModal
          open={alterarComercialOpen}
          users={workspaceUsers}
          submitting={alterarComercialSubmitting}
          onClose={() => !alterarComercialSubmitting && setAlterarComercialOpen(false)}
          onConfirm={handleConfirmAlterarComercial}
        />
      )}

      {canReassignComercial && (
        <CccReverterModal
          open={Boolean(reverterTargetId)}
          empresa={reverterMarcacao?.nome_empresa}
          users={comercialUsers}
          submitting={revertendoComercial}
          currentComercialUserId={
            reverterMarcacao?.assignees?.[0]?.id ??
            (() => {
              const raw = (reverterMarcacao?.form_data as Record<string, unknown> | undefined)
                ?.marcacaoUsers;
              const first = Array.isArray(raw) ? raw[0] : null;
              const n = Number(first);
              return Number.isFinite(n) ? n : null;
            })()
          }
          onClose={() => !revertendoComercial && setReverterTargetId(null)}
          onConfirm={handleConfirmReverter}
        />
      )}

      <RecuperacaoInvestigateModal
        open={investigateOpen}
        onClose={closeInvestigateModal}
        onOpenMarcacao={(row) => void handleInvestigateOpenMarcacao(row)}
        quickSearchSeed={investigateQuickSearch}
      />
    </div>
  );
};

export default RecuperacaoBoard;
